import { z } from "zod";

// biome-ignore lint/suspicious/noControlCharactersInRegex: Control characters are intentionally matched for sanitization
const CONTROL_CHAR_REGEX = /[\u0000-\u001F\u007F]/g;
const WHITESPACE_REGEX = /\s+/g;
// biome-ignore lint/suspicious/noControlCharactersInRegex: Control characters are intentionally matched for validation
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001F]/;
const ISO_DATE_FORMAT = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const DANGEROUS_TAG_REGEX =
	/<\/?\s*(script|style|iframe|object|embed|link|meta|svg|math|form|input|button|textarea|select|template|video|audio|source)\b[^>]*>/i;
const ANGLE_BRACKET_REGEX = /[<>]/;
const EVENT_HANDLER_REGEX = /on[a-z]+\s*=/i;
const JAVASCRIPT_PROTOCOL_REGEX = /javascript:/i;

const normalizeInput = (value: string): string =>
	value
		.normalize("NFKC")
		.replace(CONTROL_CHAR_REGEX, "")
		.replace(WHITESPACE_REGEX, " ")
		.trim();

const preprocessString = (
	value: unknown,
	emptyToUndefined = false,
): unknown => {
	if (typeof value !== "string") {
		return value;
	}
	const normalized = normalizeInput(value);
	if (emptyToUndefined && normalized === "") {
		return undefined;
	}
	return normalized;
};

const isRealIsoDate = (value: string): boolean => {
	if (!ISO_DATE_FORMAT.test(value)) {
		return false;
	}
	const date = new Date(`${value}T00:00:00.000Z`);
	if (Number.isNaN(date.getTime())) {
		return false;
	}
	const [year, month, day] = value.split("-").map(Number);
	return (
		date.getUTCFullYear() === year &&
		date.getUTCMonth() + 1 === month &&
		date.getUTCDate() === day
	);
};

type StringSchemaOptions = {
	min?: number;
	max?: number;
	message?: string;
	allowHtml?: boolean;
	htmlMessage?: string;
};

const containsDangerousHtml = (value: string): boolean => {
	if (!value) {
		return false;
	}
	return (
		DANGEROUS_TAG_REGEX.test(value) ||
		ANGLE_BRACKET_REGEX.test(value) ||
		EVENT_HANDLER_REGEX.test(value) ||
		JAVASCRIPT_PROTOCOL_REGEX.test(value)
	);
};

const applyHtmlPolicy = (
	schema: z.ZodString,
	options?: StringSchemaOptions,
): z.ZodString => {
	if (options?.allowHtml) {
		return schema;
	}
	const message =
		options?.htmlMessage ??
		options?.message ??
		"HTML-like characters are not allowed";
	return schema.refine((value) => !containsDangerousHtml(value), {
		message,
	});
};

export const sanitizedRequiredString = (options?: StringSchemaOptions) =>
	z.preprocess(
		(value: unknown) => preprocessString(value),
		applyHtmlPolicy(
			z
				.string()
				.min(options?.min ?? 1, { message: options?.message })
				.max(options?.max ?? 255),
			options,
		),
	);

export const sanitizedOptionalString = (options?: StringSchemaOptions) =>
	z.preprocess(
		(value: unknown) => preprocessString(value, true),
		applyHtmlPolicy(
			z
				.string()
				.min(options?.min ?? 1, { message: options?.message })
				.max(options?.max ?? 255),
			options,
		).optional(),
	);

export const sanitizedEmailSchema = z.preprocess((value: unknown) => {
	if (typeof value !== "string") {
		return value;
	}
	return normalizeInput(value).toLowerCase();
}, z.string().email());

export const sanitizedNameSchema = (options?: StringSchemaOptions) =>
	sanitizedRequiredString({ min: options?.min ?? 2, max: options?.max ?? 120 });

export const sanitizedLongTextSchema = (max = 1024) =>
	sanitizedOptionalString({ min: 1, max });

export const optionalIsoDateStringSchema = z.preprocess((value: unknown) => {
	if (value === undefined || value === null) {
		return undefined;
	}
	if (value instanceof Date) {
		if (Number.isNaN(value.getTime())) {
			return undefined;
		}
		return value.toISOString().slice(0, 10);
	}
	if (typeof value === "string") {
		const normalized = normalizeInput(value);
		return normalized === "" ? undefined : normalized;
	}
	return value;
}, z
	.string()
	.regex(ISO_DATE_FORMAT, "Date must use YYYY-MM-DD format")
	.refine(isRealIsoDate, "Date is invalid")
	.optional());

const baseFileNameSchema = z
	.string()
	.min(1, { message: "Name is required" })
	.max(128, { message: "Name must be 128 characters or fewer" })
	.refine((value: string) => !INVALID_FILENAME_CHARS.test(value), {
		message: "Name contains invalid characters",
	})
	.refine((value: string) => value !== "" && value !== ".." && value !== ".", {
		message: "Name is not allowed",
	});

export const fileNameSchema = z.preprocess(
	(value: unknown) => preprocessString(value),
	baseFileNameSchema,
);

export const folderNameSchema = fileNameSchema;

export const searchQuerySchema = sanitizedRequiredString({ min: 0, max: 256 });

export const sanitizeInputString = (value: string): string =>
	normalizeInput(value);

export const sanitizedNullableString = (options?: StringSchemaOptions) =>
	sanitizedOptionalString(options).or(z.literal(null));

/** ISO 3166-1 alpha-2 country code (e.g. "FI"). Shared by billing and
 * project site addresses. */
export const isoCountryCodeSchema = sanitizedNullableString({
	min: 2,
	max: 2,
}).refine(
	(value: string | null | undefined) => !value || /^[A-Z]{2}$/.test(value),
	{ message: "Country must use ISO 3166-1 alpha-2" },
);
